import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { Get, Send, Remove } from '../../services/service.api';
import { toast } from 'react-toastify';
import FullScreenLoader from '../../components/FullScreenLoader';
import AnimatedNumber from '../../components/component.animatedNumber';
import ModalCagnotte from './components/ModalCagnotte';
import {
  PiggyBank, Plus, Trash2, Users, Coins, ArrowRight,
  TrendingUp, TrendingDown, Crown
} from 'lucide-react';

function CagnotteCard({ cagnotte, index, userId, onDelete, onClick }) {
  const isAdmin = cagnotte.admin_id === userId;
  const solde = cagnotte.solde ?? 0;
  const isPositive = solde >= 0;
  const collected = Number(cagnotte.total_collecte || 0);
  const spent = Number(cagnotte.total_depenses || 0);
  const flow = collected + spent;
  const spentPct = flow > 0 ? Math.round((spent / flow) * 100) : 0;
  const active = cagnotte.statut === 'active';

  const target = cagnotte.objectif_mensuel > 0
    ? Math.min((collected / (cagnotte.objectif_mensuel * (cagnotte.nb_membres || 1))) * 100, 100)
    : null;

  return (
    <article
      className={`cg-card ${active ? 'active' : ''}`}
      style={{ '--i': index }}
      onClick={() => onClick(cagnotte.id)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick(cagnotte.id))}
      tabIndex={0}
      role="button"
      aria-label={`Ouvrir la cagnotte ${cagnotte.libelle}`}
    >
      <header className="cg-head">
        <div className="cg-icon"><PiggyBank size={20} /></div>
        <div className="cg-title">
          <h3>{cagnotte.libelle}</h3>
          <div className="cg-tags">
            <span className={`badge ${active ? 'badge-success' : 'badge-gold'}`}>{cagnotte.statut}</span>
            {isAdmin && <span className="badge badge-gold"><Crown size={10} /> Admin</span>}
          </div>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="cg-delete"
            onClick={e => { e.stopPropagation(); onDelete(cagnotte); }}
            title="Supprimer"
            aria-label="Supprimer la cagnotte"
          >
            <Trash2 size={15} />
          </button>
        )}
      </header>

      {cagnotte.description && <p className="cg-desc">{cagnotte.description}</p>}

      {/* Hero balance */}
      <div className="cg-balance">
        <span className="cg-balance-label">Solde disponible</span>
        <span className={`cg-balance-value ${isPositive ? 'pos' : 'neg'}`}>
          <AnimatedNumber value={solde} format={n => `${n >= 0 ? '' : '−'}${Math.abs(n).toLocaleString('fr-FR')}`} />
          <small> {cagnotte.devise || 'FCFA'}</small>
        </span>
      </div>

      {/* In / out */}
      <div className="cg-flow">
        <div className="cg-flow-item in">
          <TrendingUp size={14} />
          <div>
            <strong><AnimatedNumber value={collected} /></strong>
            <span>Collecté</span>
          </div>
        </div>
        <div className="cg-flow-item out">
          <TrendingDown size={14} />
          <div>
            <strong><AnimatedNumber value={spent} /></strong>
            <span>Dépensé</span>
          </div>
        </div>
      </div>
      <div className="cg-split" title={`${spentPct}% dépensé`}>
        <div className="cg-split-in" style={{ '--w': `${100 - spentPct}%` }} />
        <div className="cg-split-out" style={{ '--w': `${spentPct}%` }} />
      </div>

      {target !== null && (
        <div className="cg-target">
          <div><span>Objectif</span><span>{target.toFixed(0)}%</span></div>
          <div className="cm-progress"><div className="cm-progress-fill" style={{ '--w': `${target}%` }} /></div>
        </div>
      )}

      <footer className="cg-foot">
        <span><Users size={13} /> {cagnotte.nb_membres || 0} membres</span>
        <span><Coins size={13} /> {cagnotte.nb_cotisations || 0} cotisations</span>
        <ArrowRight size={16} className="cg-arrow" />
      </footer>
    </article>
  );
}

export default function CagnotteList() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const navigate = useNavigate();

  const [cagnottes, setCagnottes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Get('/cagnottes', token);
      if (Array.isArray(res)) {
        // Read from localStorage to avoid stale closure during the 'company-switched' event
        const currentCompanyId = localStorage.getItem('activeCompanyId') || activeCompany?.id;
        const filtered = currentCompanyId 
          ? res.filter(c => c.company_id === currentCompanyId)
          : res;
        setCagnottes(filtered);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [token, activeCompany?.id]);

  useEffect(() => {
    setCagnottes([]);
    load();
  }, [load]);

  useEffect(() => {
    const handleSwitch = () => load();
    window.addEventListener('company-switched', handleSwitch);
    return () => window.removeEventListener('company-switched', handleSwitch);
  }, [load]);

  const handleCreate = async (data) => {
    if (activeCompany?.id) {
      data.company_id = activeCompany.id;
    }
    const res = await Send('/cagnottes', data, true, token);
    if (res?.cagnotte) {
      toast.success('Cagnotte créée !');
      load();
    } else {
      toast.error(res?.error || 'Erreur lors de la création');
      throw new Error(res?.error);
    }
  };

  const handleDelete = async (cagnotte) => {
    if (!window.confirm(t.cagnotte.deleteConfirm)) return;
    const res = await Remove(`/cagnottes/${cagnotte.id}`, token);
    if (res?.message) {
      toast.success('Cagnotte supprimée');
      setCagnottes(c => c.filter(x => x.id !== cagnotte.id));
    } else {
      toast.error(res?.error || 'Erreur');
    }
  };

  const totalBalance = cagnottes.reduce((acc, c) => acc + (c.solde || 0), 0);
  const totalCollected = cagnottes.reduce((acc, c) => acc + (c.total_collecte || 0), 0);

  return (
    <div className="page-content page-enter">
      {loading && <FullScreenLoader message="Chargement des cagnottes..." />}

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PiggyBank size={24} style={{ color: 'var(--gold)' }} />
            {t.cagnotte.title}
          </h1>
          <p>{t.cagnotte.subtitle}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModalOpen(true)} id="btn-new-cagnotte">
          <Plus size={16} />
          {t.cagnotte.add}
        </button>
      </div>

      {/* Summary KPIs */}
      {cagnottes.length > 0 && (
        <div className="kpi-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Cagnottes actives', num: cagnottes.filter(c => c.statut === 'active').length, icon: PiggyBank, color: 'var(--primary)', bg: 'rgba(16,185,129,0.1)' },
            { label: 'Total collecté', num: totalCollected, icon: TrendingUp, color: 'var(--success)', bg: 'rgba(16,185,129,0.1)', suffix: ' FCFA' },
            { label: 'Solde global', num: totalBalance, signed: true, icon: TrendingDown, color: totalBalance >= 0 ? 'var(--gold)' : 'var(--danger)', bg: 'rgba(245,197,24,0.1)', suffix: ' FCFA' },
          ].map((kpi, i) => (
            <div key={i} className="kpi-card" style={{ '--kpi-color': kpi.color, '--kpi-bg': kpi.bg }}>
              <div className="kpi-header">
                <div className="kpi-icon"><kpi.icon size={20} /></div>
              </div>
              <div>
                <div className="kpi-value number-format"><AnimatedNumber value={kpi.num} format={n => `${kpi.signed && n >= 0 ? '+' : ''}${n.toLocaleString('fr-FR')}`} />{kpi.suffix || ''}</div>
                <div className="kpi-label">{kpi.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      {!loading && cagnottes.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <PiggyBank size={48} style={{ color: 'var(--text-muted)', marginBottom: 16, opacity: 0.4 }} />
          <h3 style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{t.cagnotte.noCagnottes}</h3>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModalOpen(true)}>
            <Plus size={16} /> {t.cagnotte.add}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 20 }}>
          {cagnottes.map((c, i) => (
            <CagnotteCard
              key={c.id}
              index={i}
              cagnotte={c}
              userId={user?.id}
              onDelete={handleDelete}
              onClick={(id) => navigate(`/cagnotte/${id}`)}
            />
          ))}
        </div>
      )}

      <ModalCagnotte
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleCreate}
      />
    </div>
  );
}
